import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

// Interfaces for Daily Reports
export interface LabourDailyReportDetail {
  id?: number;
  sub_contractor_name?: string;
  mason?: number;
  female_coolie?: number;
  male_coolie?: number;
  carpenter?: number;
  fitter?: number;
  fitter_helper?: number;
  surveyor?: number;
  treader?: number;
  others?: number;
  total_manhours?: number;
  notes?: string;
}

export interface LabourDailyReport {
  id?: number;
  project_id: number;
  report_date: string;
  contractor_name?: string;
  client_name?: string;
  shift_type?: string;
  hindrance?: string;
  created_by?: number;
  created_at?: string;
  updated_at?: string;
  org_id?: number;
  branch_id?: number;
  project_name?: string;
  project_identifier?: string;
  created_by_name?: string;
  details?: LabourDailyReportDetail[];
  totals?: {
    mason: number;
    female_coolie: number;
    male_coolie: number;
    carpenter: number;
    fitter: number;
    fitter_helper: number;
    surveyor: number;
    treader: number;
    others: number;
    total: number;
  };
}

export interface CreateDailyReport {
  project_id: number;
  report_date: string;
  contractor_name?: string;
  client_name?: string;
  shift_type?: string;
  hindrance?: string;
  details?: LabourDailyReportDetail[];
}

export interface UpdateDailyReport {
  report_date?: string;
  contractor_name?: string;
  client_name?: string;
  shift_type?: string;
  hindrance?: string;
  details?: LabourDailyReportDetail[];
}

// Interfaces for Mobilization Plans
export interface LabourMobilizationPlan {
  id?: number;
  project_id: number;
  plan_date: string;
  carpenders?: number;
  carpenders_helpers?: number;
  rein_fitters?: number;
  rein_fitters_helpers?: number;
  masons?: number;
  masons_helpers?: number;
  category_data?: Record<string, number>;
  total_available?: number;
  committed_to_increase?: number;
  expected_total_strength?: number;
  shortage_against_committed?: number;
  net_increase?: number;
  notes?: string;
  created_by?: number;
  created_at?: string;
  updated_at?: string;
  org_id?: number;
  branch_id?: number;
  project_name?: string;
  project_identifier?: string;
  created_by_name?: string;
}

export interface CreateMobilizationPlan {
  project_id: number;
  plan_date: string;
  carpenders?: number;
  carpenders_helpers?: number;
  rein_fitters?: number;
  rein_fitters_helpers?: number;
  masons?: number;
  masons_helpers?: number;
  category_data?: Record<string, number>;
  committed_to_increase?: number;
  expected_total_strength?: number;
  net_increase?: number;
  notes?: string;
}

export interface UpdateMobilizationPlan {
  plan_date?: string;
  carpenders?: number;
  carpenders_helpers?: number;
  rein_fitters?: number;
  rein_fitters_helpers?: number;
  masons?: number;
  masons_helpers?: number;
  category_data?: Record<string, number>;
  committed_to_increase?: number;
  expected_total_strength?: number;
  net_increase?: number;
  notes?: string;
}

export interface PaginationResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_items: number;
    items_per_page: number;
  };
}

export interface LabourStatistics {
  daily_reports: {
    total_reports: number;
    total_projects: number;
    total_mason: number;
    total_female_coolie: number;
    total_male_coolie: number;
    total_carpenter: number;
    total_fitter: number;
    total_fitter_helper: number;
    total_surveyor: number;
    total_treader: number;
    total_others: number;
    grand_total_manhours: number;
  };
  mobilization_plans: {
    total_plans: number;
    avg_available: number;
    avg_expected: number;
    avg_shortage: number;
    total_net_increase: number;
  };
}

export interface CategoryDeviation {
  planned: number;
  actual: number;
  deviation: number;
  deviation_percent: number;
  status: 'excess' | 'shortage';
}

export interface DeviationReportItem {
  plan_id: number;
  report_id: number | null;
  project_id: number;
  project_name: string;
  project_identifier: string;
  plan_date: string;
  report_date: string | null;
  carpenters: CategoryDeviation;
  fitters: CategoryDeviation;
  masons: CategoryDeviation;
  total: CategoryDeviation;
  planned_expected_total: number;
}

export interface DeviationReportData {
  group_by: 'date' | 'project';
  reports: DeviationReportItem[] | any[];
  summary: {
    total_records: number;
    total_projects: number;
    total_carpenters_deviation: number;
    total_fitters_deviation: number;
    total_masons_deviation: number;
    total_deviation: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class LabourMobilizationService {
  private apiUrl = `${environment.BASE_URL}labour-mobilization`;

  constructor(private http: HttpClient) {}

  // ========== DAILY REPORTS ==========

  getDailyReports(params?: {
    project_id?: number;
    start_date?: string;
    end_date?: string;
    shift_type?: string;
    page?: number;
    limit?: number;
    search?: string;
  }): Observable<PaginationResponse<LabourDailyReport>> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        const value = (params as any)[key];
        if (value !== null && value !== undefined) {
          httpParams = httpParams.set(key, value.toString());
        }
      });
    }
    return this.http.get<PaginationResponse<LabourDailyReport>>(`${this.apiUrl}/daily-reports`, { params: httpParams });
  }

  getDailyReportById(id: number): Observable<{ success: boolean; data: LabourDailyReport }> {
    return this.http.get<{ success: boolean; data: LabourDailyReport }>(`${this.apiUrl}/daily-reports/${id}`);
  }

  createDailyReport(report: CreateDailyReport): Observable<any> {
    return this.http.post(`${this.apiUrl}/daily-reports`, report);
  }

  updateDailyReport(id: number, report: UpdateDailyReport): Observable<any> {
    return this.http.put(`${this.apiUrl}/daily-reports/${id}`, report);
  }

  deleteDailyReport(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/daily-reports/${id}`);
  }

  // ========== MOBILIZATION PLANS ==========

  getMobilizationPlans(params?: {
    project_id?: number;
    start_date?: string;
    end_date?: string;
    page?: number;
    limit?: number;
    search?: string;
  }): Observable<PaginationResponse<LabourMobilizationPlan>> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        const value = (params as any)[key];
        if (value !== null && value !== undefined) {
          httpParams = httpParams.set(key, value.toString());
        }
      });
    }
    return this.http.get<PaginationResponse<LabourMobilizationPlan>>(`${this.apiUrl}/mobilization-plans`, { params: httpParams });
  }

  getMobilizationPlanById(id: number): Observable<{ success: boolean; data: LabourMobilizationPlan }> {
    return this.http.get<{ success: boolean; data: LabourMobilizationPlan }>(`${this.apiUrl}/mobilization-plans/${id}`);
  }

  createMobilizationPlan(plan: CreateMobilizationPlan): Observable<any> {
    return this.http.post(`${this.apiUrl}/mobilization-plans`, plan);
  }

  updateMobilizationPlan(id: number, plan: UpdateMobilizationPlan): Observable<any> {
    return this.http.put(`${this.apiUrl}/mobilization-plans/${id}`, plan);
  }

  deleteMobilizationPlan(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/mobilization-plans/${id}`);
  }

  // ========== STATISTICS ==========

  getStatistics(params?: {
    project_id?: number;
    start_date?: string;
    end_date?: string;
  }): Observable<{ success: boolean; data: LabourStatistics }> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        const value = (params as any)[key];
        if (value !== null && value !== undefined) {
          httpParams = httpParams.set(key, value.toString());
        }
      });
    }
    return this.http.get<{ success: boolean; data: LabourStatistics }>(`${this.apiUrl}/statistics`, { params: httpParams });
  }

  // ========== DEVIATION REPORTS ==========

  getDeviationReport(params?: {
    project_id?: number;
    start_date?: string;
    end_date?: string;
    group_by?: 'date' | 'project';
  }): Observable<{ success: boolean; data: DeviationReportData }> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        const value = (params as any)[key];
        if (value !== null && value !== undefined) {
          httpParams = httpParams.set(key, value.toString());
        }
      });
    }
    return this.http.get<{ success: boolean; data: DeviationReportData }>(`${this.apiUrl}/deviation-report`, { params: httpParams });
  }

  // ========== UTILITY METHODS ==========

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatDateTime(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  calculateTotalManhours(detail: LabourDailyReportDetail): number {
    return (detail.mason || 0) +
           (detail.female_coolie || 0) +
           (detail.male_coolie || 0) +
           (detail.carpenter || 0) +
           (detail.fitter || 0) +
           (detail.fitter_helper || 0) +
           (detail.surveyor || 0) +
           (detail.treader || 0) +
           (detail.others || 0);
  }

  calculateMobilizationTotal(plan: LabourMobilizationPlan): number {
    const cd = plan.category_data;
    if (cd && typeof cd === 'object' && Object.keys(cd).length > 0) {
      return Object.values(cd).reduce((sum, v) => sum + (Number(v) || 0), 0);
    }
    return (plan.carpenders || 0) +
           (plan.carpenders_helpers || 0) +
           (plan.rein_fitters || 0) +
           (plan.rein_fitters_helpers || 0) +
           (plan.masons || 0) +
           (plan.masons_helpers || 0);
  }
}

